import { client } from '../util/client';

import { Patient, Professional } from '@aldebaran/prisma';
import { UseQueryOptions, useMutation, useQuery } from 'react-query';
import { AxiosError } from 'axios';
import {
  InitiateAuthFromPatientResponse,
  VerifyAuthFromInvitationLastNameResponse,
  VerifyAuthFromInvitationResponse,
} from '@aldebaran/common/types/api';
import { useInjectPatient } from './useInjectPatient';
import { useSelector } from 'react-redux';
import { RootState } from '../types/graph';

export type PatientForm = Pick<
  Patient,
  'firstName' | 'lastName' | 'dateOfBirth' | 'sex'
> & { self: boolean | null; phone?: Patient['phone'] } & PatientCollectedFields;

export type PatientCollectedFields = Partial<
  Pick<
    Patient,
    | 'email'
    | 'addressStreet'
    | 'addressPostalCode'
    | 'addressCity'
    | 'addressCountry'
    | 'profession'
    | 'birthName'
    | 'birthPlace'
    | 'birthPlaceInseeCode'
    | 'socialSecurityNumber'
  >
>;

export const usePatients = ({
  onSuccess,
  onError,
  onSettled,
  enabled,
  refetchOnMount,
}: {
  onSuccess?: (payload: Patient[]) => void;
  onSettled?: (payload?: Patient[]) => void;
  onError?: (error: unknown) => void;
  enabled?: boolean;
  refetchOnMount?: boolean;
}) =>
  useQuery<Patient[]>('patients', {
    queryFn: async () =>
      client
        .get<Patient[]>('/patient/authorized')
        .then((response) => response?.data),
    refetchOnMount,
    retry: false,
    onSuccess,
    onError,
    onSettled,
    enabled,
  });

export const usePatient = (
  id: string | null,
  options: UseQueryOptions<Patient, AxiosError> = {},
) => {
  return useQuery<Patient, AxiosError>(
    ['patient', id],
    async () => client(`/patient/${id}`).then((response) => response?.data),
    {
      enabled: !!id,
      ...options,
    },
  );
};

export const useSelectedPatient = () => {
  const patientId = useSelector(
    (state: RootState) => state.consultation.patient?.id,
  );
  return usePatient(patientId ?? null);
};

export const useCreatePatient = (temporary: boolean) => {
  const sessionId = useSelector((state: RootState) => state.sessionId);

  return useMutation({
    mutationFn: async (form: PatientForm) =>
      client
        .post<Patient>(temporary ? '/patient/temporary' : '/patient/', {
          ...form,
          sessionId,
        })
        .then((payload) => payload?.data),
  });
};

export const useUpdatePatient = () => {
  return useMutation({
    mutationFn: async ({
      id,
      ...form
    }: { id: Patient['id'] } & Partial<
      PatientForm & { researchConsent?: boolean } & {
        unsubscribedFromEmails?: boolean;
      } & { unsubscribedFromSMS?: boolean }
    >) =>
      client
        .put<Patient>(`/patient/${id}`, form)
        .then((payload) => payload?.data),
  });
};

export const useSelectPatient = ({
  required = true,
}: { required?: boolean } = {}) => {
  const injectPatient = useInjectPatient({ required });

  return useMutation({
    mutationFn: async (id: string | null) =>
      client(`/patient/${id}`).then((response) => response?.data),
    onSuccess: (patient: Patient) => injectPatient(patient),
  });
};

export const useInitiatePatient = () =>
  useMutation({
    mutationFn: async ({ phone }: { phone: string }) =>
      client.post<{ sessionId: string }>('/auth/patient/initiate', {
        phone,
      }),
  });

export const useVerifyPatient = () => {
  const sId = useSelector((state: RootState) => state.sessionId);

  return useMutation({
    mutationFn: async ({
      phone,
      code,
      sessionId,
    }: {
      phone: string;
      code: string;
      sessionId: string;
    }) =>
      client.post('/auth/patient/verify', {
        phone,
        sessionId,
        code,
        sId,
      }),
  });
};

export const useInitiatePatientInvitation = () =>
  useMutation({
    mutationFn: async ({ invitationId }: { invitationId: string }) =>
      client.post<InitiateAuthFromPatientResponse>(
        '/auth/patient/invitation/initiate',
        {
          invitationId,
        },
      ),
  });

export const useVerifyPatientInvitation = () => {
  const sId = useSelector((state: RootState) => state.sessionId);

  return useMutation({
    mutationFn: async ({
      invitationId,
      code,
    }: {
      invitationId: string;
      code: string;
    }) =>
      client.post<VerifyAuthFromInvitationResponse>(
        '/auth/patient/invitation/verify',
        {
          invitationId: invitationId,
          code: code,
          sId,
        },
      ),
  });
};

export const useVerifyPatientInvitationLastName = (forUnsubscribe = false) => {
  return useMutation({
    mutationFn: async ({
      invitationId,
      lastName,
    }: {
      invitationId: string;
      lastName: string;
    }) =>
      client.post<VerifyAuthFromInvitationLastNameResponse>(
        '/auth/patient/invitation/verify-last-name',
        {
          invitationId,
          lastName,
          forUnsubscribe,
        },
      ),
  });
};

export const useLogout = () =>
  useMutation({ mutationFn: async () => client.post('/auth/patient/logout') });

export const useSendPatientConfirmation = () => {
  return useMutation({
    mutationFn: async ({
      patientId,
      questionnaireId,
      professionalId,
      withChecklist = false,
    }: {
      patientId: Patient['id'];
      questionnaireId: string;
      professionalId: Professional['id'];
      withChecklist?: boolean;
    }) => {
      return client.post('/consultation/sendPatientConfirmation', {
        patientId,
        questionnaireId,
        professionalId,
        withChecklist,
      });
    },
  });
};
